import React, { useState } from 'react'
import { useMutation, useQuery } from 'react-query'

import { useEbookAction } from '@hooks/ebook/useEbookAction'
import { EbookPromotionOnSaleResponse } from '@models/ebook/EbookPromotionOnSaleResponse'
import { useWishlistAction } from '@hooks/ebook/useWishlistAction'
import cloneDeep from 'lodash.clonedeep'
import { queryClient } from '@client/init'
import { WishlisEnum } from '@models/ebook/WishlishEnum'
import { EbookLogEnum } from '@interfaces/EbookLogEnum'
import { useAuthentication } from '@hooks/useAuthentication'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import {
  EbookDiscountProps,
  EbookDiscountAcceptProps,
  FormatType,
} from './interface'

export function withEbookDiscount(Component: React.FC<EbookDiscountProps>) {
  function WithEbookDiscount(props: EbookDiscountAcceptProps) {
    const ebookClient = useEbookAction()
    const { toggleWishlist } = useWishlistAction()
    const { isAuthenticated, canFetchApi } = useAuthentication()
    const loginModal = useModal({ modal: 'login' })
    const [format] = useState<FormatType>({
      grid: [0, 1, 2, 3],
      col: 4,
    })

    const { data: datas, isLoading } = useQuery(
      ['ebook-promotion-onsale-home'],
      () => ebookClient.getEbookPromotionOnSale({ limitPerPage: 20 }),
      { enabled: canFetchApi }
    )

    const { mutateAsync: toggleWishlistEbook, isLoading: isToggleWishlist } =
      useMutation(
        (id: number) => toggleWishlist(id, EbookLogEnum.EBOOK_WISHLIST),
        {
          onSuccess: async (type, id) => {
            queryClient.setQueryData<EbookPromotionOnSaleResponse | undefined>(
              ['ebook-promotion-onsale-home'],
              (oldData?: EbookPromotionOnSaleResponse) => {
                if (oldData) {
                  const temp = cloneDeep(oldData)
                  const index = temp.data?.findIndex(row => row.id === id)
                  if (index !== -1) {
                    temp.data[index].isWishlist = type === WishlisEnum.CREATE
                    temp.data[index].wishlistCount =
                      type === WishlisEnum.CREATE
                        ? temp.data[index].wishlistCount + 1
                        : temp.data[index].wishlistCount - 1
                    return temp
                  }

                  return oldData
                }

                return oldData
              }
            )
          },
        }
      )

    async function handleWishlist(id: number) {
      if (isAuthenticated) {
        await toggleWishlistEbook(id)
      } else {
        loginModal.show()
      }
    }

    const componentProps = {
      ...props,
      datas: datas?.data || [],
      isLoading,
      format,
      is1Row: datas ? datas.data.length <= 2 : false,
      isToggleWishlist,
      handleWishlist,
    }

    return <Component {...componentProps} />
  }

  return WithEbookDiscount
}
